import { useEffect, useState } from 'react';
import { toast } from '@/components/ui/use-toast';

export function useConnection(
	socket: any,
	options: { latencyThreshold?: number; sampleSize?: number; notifyOnUnstable?: boolean } = {}
) {
	const { latencyThreshold = 300, sampleSize = 5, notifyOnUnstable = false } = options;
	const [stable, setStable] = useState(true);
	const [, setSamples] = useState<number[]>([]);
	const [netStable, setNetStable] = useState(true);
	const isStable = stable && netStable;

	useEffect(() => {
		if (!notifyOnUnstable || isStable) return;

		toast({
			title: 'Connection unstable',
			description: 'High or fluctuating network latency detected.',
			variant: 'destructive',
		});
	}, [notifyOnUnstable, isStable]);

	// Socket.IO ping/pong sampling
	useEffect(() => {
		// Ensure underlying engine supports ping/pong
		if (!socket?.io?.engine) return;

		let pingTs: number;
		const onPing = () => {
			pingTs = performance.now();
		};
		const onPong = () => {
			const latency = performance.now() - pingTs;
			setSamples((prev: any) => {
				const window = [...prev, latency].slice(-sampleSize);
				const avg = window.reduce((sum: any, v: any) => sum + v, 0) / window.length;
				setStable(avg < latencyThreshold);
				return window;
			});
		};

		socket.io.engine.on('ping', onPing);
		socket.io.engine.on('pong', onPong);

		return () => {
			socket.io.engine.off('ping', onPing);
			socket.io.engine.off('pong', onPong);
		};
	}, [socket, latencyThreshold, sampleSize]);

	// Network Information API for effectiveType
	useEffect(() => {
		const conn = (navigator as any).connection;
		if (!conn) return;

		const check = () => {
			// treat only '4g' as stable
			setNetStable(conn.effectiveType === '4g');
		};

		conn.addEventListener('change', check);
		check();
		return () => {
			conn.removeEventListener('change', check);
		};
	}, []);

	return isStable;
}
